import { useEffect, useState } from 'react';
import { api } from '../api';
import type {
  DocumentBlock, GeneratedLeaseDocument, GenerationOverview, PreviewDocument,
} from '../types';

/** Rendu à l'écran d'un bloc du document, au plus près de ce qui sera imprimé. */
function Block({ block }: { block: DocumentBlock }) {
  switch (block.kind) {
    case 'title':
      return (
        <header className="doc-title">
          <h4>{block.text}</h4>
          {block.subtitle && <p>{block.subtitle}</p>}
          {block.note && <p className="doc-note">{block.note}</p>}
        </header>
      );
    case 'section':
      return <h5 className="doc-section">{block.number}. {block.title}</h5>;
    case 'heading':
      return <h6 className="doc-heading">{block.text}</h6>;
    case 'paragraph':
      return <p className={block.note ? 'doc-note' : 'doc-paragraph'}>{block.text}</p>;
    case 'fields':
      return (
        <dl className={block.boxed ? 'doc-fields boxed' : 'doc-fields'}>
          {block.rows.map((row) => (
            <div key={`${row.label}-${row.value}`}>
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
      );
    case 'list':
      return <ul className="doc-list">{block.items.map((item) => <li key={item}>{item}</li>)}</ul>;
    case 'amounts':
      return (
        <table className="doc-amounts">
          <caption>{block.caption}</caption>
          <tbody>
            {block.rows.map((row) => (
              <tr key={row.label} className={row.strong ? 'strong' : undefined}>
                <th scope="row">{row.label}</th>
                <td>{row.amount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    case 'callout':
      return <p className={block.strong ? 'doc-callout strong' : 'doc-callout'}>{block.text}</p>;
    case 'handwriting':
      return (
        <div className="doc-handwriting">
          <strong>{block.instruction}</strong>
          <span>{block.lines} lignes vierges à remplir de la main de la caution.</span>
        </div>
      );
    case 'signatures':
      return (
        <p className="doc-signatures">
          Fait à {block.place}, le {block.date}. Signatures : {block.columns.map((column) => column.label).join(' · ')}
        </p>
      );
    default:
      return null;
  }
}

export function LeaseGeneration({ leaseId, onGenerated }: { leaseId: string; onGenerated: () => void }) {
  const [overview, setOverview] = useState<GenerationOverview | null>(null);
  const [preview, setPreview] = useState<PreviewDocument[] | null>(null);
  const [openDocument, setOpenDocument] = useState<string>('lease');
  const [generated, setGenerated] = useState<GeneratedLeaseDocument[] | null>(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  // Parcours en quatre temps plutôt qu'un formulaire unique : Alan s'était fait
  // piéger en envoyant à signature des documents pas encore produits. L'ordre
  // des étapes rend cet enchaînement évident.
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);

  async function load(): Promise<void> {
    try {
      setOverview(await api<GenerationOverview>(`/api/admin/leases/${leaseId}/generation`));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Chargement impossible.');
    }
  }

  useEffect(() => {
    setPreview(null);
    setGenerated(null);
    setMessage('');
    setError('');
    setStep(1);
    void load();
  }, [leaseId]);

  // L'étape de relecture affiche le document sans qu'on ait à le demander :
  // c'est tout son objet.
  useEffect(() => {
    if (step === 4 && overview && !preview) void showPreview();
  }, [step, overview]);

  function patch(changes: Partial<GenerationOverview>): void {
    setOverview((current) => (current ? { ...current, ...changes } : current));
    setMessage('');
  }

  async function save(): Promise<void> {
    if (!overview) return;
    setBusy(true);
    setError('');
    try {
      await api(`/api/admin/leases/${leaseId}/generation`, {
        method: 'PUT',
        body: JSON.stringify({
          clauses: overview.clauses.map((clause) => ({
            code: clause.code,
            active: clause.active,
            body: clause.body,
          })),
          annexes: overview.annexes.map((annex) => ({ code: annex.code, included: annex.included })),
          paymentStartDate: overview.paymentStart.date || null,
          paymentStartNote: overview.paymentStart.note,
        }),
      });
      setMessage('Sélection enregistrée.');
      setPreview(null);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Enregistrement impossible.');
    } finally {
      setBusy(false);
    }
  }

  async function showPreview(): Promise<void> {
    setBusy(true);
    setError('');
    try {
      const result = await api<{ documents: PreviewDocument[] }>(`/api/admin/leases/${leaseId}/generation/preview`);
      setPreview(result.documents);
      setOpenDocument(result.documents[0]?.key ?? 'lease');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Aperçu impossible.');
    } finally {
      setBusy(false);
    }
  }

  async function generate(): Promise<void> {
    setBusy(true);
    setError('');
    try {
      const result = await api<{ documents: GeneratedLeaseDocument[] }>(
        `/api/admin/leases/${leaseId}/generation`,
        { method: 'POST' },
      );
      setGenerated(result.documents);
      setMessage(`${result.documents.length} document(s) produits et figés.`);
      onGenerated();
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Génération impossible.');
    } finally {
      setBusy(false);
    }
  }

  if (!overview) {
    return (
      <section className="lease-generation">
        <h3>Génération des documents</h3>
        {error ? <div className="inline-message error" role="alert">{error}</div> : <p className="muted">Chargement…</p>}
      </section>
    );
  }

  const blocked = overview.controls.blocking.length > 0;
  const shown = preview?.find((document) => document.key === openDocument) ?? preview?.[0] ?? null;

  return (
    <section className="lease-generation">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Contrat</p>
          <h3>Génération des documents</h3>
        </div>
        <span className="record-code">modèle v{overview.templateVersion}</span>
      </div>
      <p className="section-intro">
        Les documents produits ici sont figés : empreinte enregistrée, clauses verrouillées. Régénérer remplace
        les exemplaires non signés, jamais un document déjà signé.
      </p>

      <ol className="wizard-steps">
        {([
          [1, 'Virements'],
          [2, 'Clauses'],
          [3, 'Annexes'],
          [4, 'Relecture'],
        ] as const).map(([value, label]) => (
          <li key={value}>
            <button
              type="button"
              className={step === value ? 'active' : step > value ? 'done' : ''}
              onClick={() => setStep(value)}
            >
              <b>{value}</b>{label}
            </button>
          </li>
        ))}
      </ol>

      {overview.controls.blocking.length > 0 && (
        <ul className="alert-list">
          {overview.controls.blocking.map((problem) => <li key={problem}>{problem}</li>)}
        </ul>
      )}
      {overview.controls.warnings.length > 0 && (
        <ul className="alert-list">
          {overview.controls.warnings.map((problem) => <li className="info" key={problem}>{problem}</li>)}
        </ul>
      )}

      {step === 1 && (
      <fieldset className="config-group">
        <legend>Début des virements</legend>
        <p className="section-intro">
          À renseigner seulement si le premier mois a déjà été réglé autrement : le bail garde sa date de prise
          d'effet ({overview.paymentStart.effectiveFrom}), seule l'attestation de virement porte celle-ci.
        </p>
        <div className="form-grid">
          <label>Premier virement
            <input
              type="date"
              value={overview.paymentStart.date ?? ''}
              min={overview.paymentStart.effectiveFrom}
              onChange={(event) => patch({
                paymentStart: { ...overview.paymentStart, date: event.target.value || null },
              })}
            />
          </label>
          <label>Précision imprimée (facultative)
            <input
              type="text"
              maxLength={600}
              placeholder="ex. le mois d'août a été réglé à la réservation"
              value={overview.paymentStart.note}
              onChange={(event) => patch({
                paymentStart: { ...overview.paymentStart, note: event.target.value },
              })}
            />
          </label>
        </div>
      </fieldset>
      )}

      {step === 2 && (
      <fieldset className="config-group">
        <legend>Clauses</legend>
        {overview.clauses.map((clause) => (
          <div className="clause-row" key={clause.code}>
            <label className="allocation-pick">
              <input
                type="checkbox"
                checked={clause.active}
                onChange={(event) => patch({
                  clauses: overview.clauses.map((current) => (current.code === clause.code
                    ? { ...current, active: event.target.checked }
                    : current)),
                })}
              />
              <span>
                {clause.title}
                <small>section {clause.section}</small>
              </span>
            </label>
            {clause.review && <p className="warn-text">{clause.review}</p>}
            {clause.active && (
              <details>
                <summary>Texte imprimé</summary>
                <textarea
                  rows={5}
                  value={clause.body}
                  onChange={(event) => patch({
                    clauses: overview.clauses.map((current) => (current.code === clause.code
                      ? { ...current, body: event.target.value }
                      : current)),
                  })}
                />
                {clause.body !== clause.defaultBody && (
                  <button
                    type="button"
                    className="button secondary compact"
                    onClick={() => patch({
                      clauses: overview.clauses.map((current) => (current.code === clause.code
                        ? { ...current, body: current.defaultBody }
                        : current)),
                    })}
                  >
                    Revenir au texte du modèle
                  </button>
                )}
              </details>
            )}
          </div>
        ))}
      </fieldset>
      )}

      {step === 3 && (
      <fieldset className="config-group">
        <legend>Annexes jointes à cet exemplaire</legend>
        <p className="section-intro">
          Cochez ce qui est réellement joint. Décocher une annexe obligatoire retire sa mention du contrat,
          pas l'obligation de fournir la pièce.
        </p>
        {overview.annexes.map((annex) => (
          <label className="allocation-pick annex-row" key={annex.code}>
            <input
              type="checkbox"
              checked={annex.included}
              onChange={(event) => patch({
                annexes: overview.annexes.map((current) => (current.code === annex.code
                  ? { ...current, included: event.target.checked }
                  : current)),
              })}
            />
            <span>
              {annex.label}
              {annex.mandatory && <em className="status draft">obligatoire</em>}
              <small>{annex.note}</small>
            </span>
          </label>
        ))}
      </fieldset>
      )}

      <div className="record-actions wizard-actions">
        {step > 1 && (
          <button type="button" className="text-button" disabled={busy} onClick={() => setStep((current) => (current - 1) as 1 | 2 | 3)}>
            Retour
          </button>
        )}
        {step < 4 ? (
          <button
            type="button"
            className="button primary compact"
            disabled={busy}
            onClick={async () => {
              // Chaque étape enregistre avant de passer à la suivante : on ne
              // perd pas une saisie en naviguant, et la relecture porte
              // toujours sur ce qui est réellement enregistré.
              await save();
              setStep((current) => (current + 1) as 2 | 3 | 4);
              await showPreview();
            }}
          >
            Enregistrer et continuer
          </button>
        ) : (
          <>
            <button type="button" className="button secondary compact" disabled={busy} onClick={() => void showPreview()}>
              Rafraîchir l'aperçu
            </button>
            <button
              type="button"
              className="button primary compact"
              disabled={busy || blocked}
              title={blocked ? 'Corrigez les points bloquants avant de produire les documents.' : undefined}
              onClick={() => void generate()}
            >
              Produire les documents
            </button>
          </>
        )}
      </div>

      {message && <div className="inline-message success">{message}</div>}
      {error && <div className="inline-message error" role="alert">{error}</div>}

      {generated && (
        <div className="record-list">
          {generated.map((document) => (
            <article className="record-card" key={document.id}>
              <div className="record-heading">
                <div>
                  <strong>{document.title}</strong>
                  <span className="record-code">{document.sha256.slice(0, 16)}…</span>
                </div>
                <a href={`/api/files/${document.id}`} target="_blank" rel="noreferrer">Ouvrir</a>
              </div>
            </article>
          ))}
        </div>
      )}

      {preview && shown && (
        <div className="doc-preview">
          <div className="tab-bar">
            {preview.map((document) => (
              <button
                type="button"
                key={document.key}
                className={document.key === openDocument ? 'active' : undefined}
                onClick={() => setOpenDocument(document.key)}
              >
                {document.title}
              </button>
            ))}
          </div>
          <article className="doc-sheet">
            {shown.blocks.map((block, index) => <Block block={block} key={`${block.kind}-${index}`} />)}
          </article>
        </div>
      )}
    </section>
  );
}
